import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  BillingInformationModel,
  StripeAchPaymentSourceModel,
} from '../../../api/billing-types';
import { BillingKeys, verifyAchDeposits } from '../../../api/billing-api';
import { Controller, useForm } from 'react-hook-form';
import VerifyDeposits from '../../../assets/verify-deposits.png';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface VerifyBankAccountDialogProps {
  open: boolean;
  handleClose: () => void;
  billingInfoModel: BillingInformationModel;
  selectedMethod: StripeAchPaymentSourceModel;
}

export default function VerifyBankAccountDialog({
  open,
  handleClose,
  selectedMethod,
  billingInfoModel,
}: VerifyBankAccountDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: { depositOne: string; depositTwo: string }) =>
      verifyAchDeposits(
        billingInfoModel,
        selectedMethod,
        data.depositOne,
        data.depositTwo
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Bank account verified successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.CustomerBillingInformation],
      });

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue verifying the bank account. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.payments.verify_bank_account, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.payments,
      });
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      depositOne: '',
      depositTwo: '',
    },
  });

  const onSubmit = (data: { depositOne: string; depositTwo: string }) => {
    //  validation requires it to be between 0.01 and 0.99, so we know . will be there
    mutate({
      depositOne: data.depositOne.split('.')[1],
      depositTwo: data.depositTwo.split('.')[1],
    });
  };

  const validateAmount = (value: string) => {
    const amount = parseFloat(value);
    if (isNaN(amount)) return 'Please enter a valid amount';
    if (amount < 0.01) return 'Amount must be at least $0.01';
    if (amount >= 1.0) return 'Amount must be less than $1.00';
    return true;
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Verify Bank Account"
      dialogContent={
        <Box>
          <Typography>
            Look for two deposits of less than $1.00 with the description
            "ACCTVERIFY:" within two days of payment method creation and enter
            them below.
          </Typography>
          <Stack direction="row" alignItems="center" gap={2} sx={{ p: 1 }}>
            <Stack gap={1} sx={{ mt: 2, width: 300 }}>
              <Controller
                name="depositOne"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: validateAmount,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      step: '0.01',
                      min: 0.01,
                      max: 0.99,
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      validateAmount(e.target.value);
                    }}
                    label="First Deposit"
                    error={!!errors.depositOne}
                    helperText={errors.depositOne?.message || ' '}
                    size="small"
                    fullWidth
                    required
                    placeholder="0.32"
                    InputProps={{
                      startAdornment: <span>$</span>,
                    }}
                  />
                )}
              />
              <Controller
                name="depositTwo"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: validateAmount,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      step: '0.01',
                      min: 0.01,
                      max: 0.99,
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      validateAmount(e.target.value);
                    }}
                    label="Second Deposit"
                    error={!!errors.depositTwo}
                    helperText={errors.depositTwo?.message || ' '}
                    size="small"
                    fullWidth
                    required
                    placeholder="0.45"
                    InputProps={{
                      startAdornment: <span>$</span>,
                    }}
                  />
                )}
              />
            </Stack>
            <Box>
              <img
                src={VerifyDeposits}
                alt="Verify Deposits"
                style={{ width: 300, height: 'auto' }}
              />
            </Box>
          </Stack>
        </Box>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isDirty,
        title: 'Verify Bank Account',
      }}
    />
  );
}
